import React, {useEffect, useState} from 'react';
import {
  GeneratedVideoChunk,
  ChunkArtifact,
} from '../../../data/models/LibraryFile/GeneratedVideoChunk';
import {MediaFile} from '../../../data/models/LibraryFile/MediaFile';
import {VideoEditorText} from '../VideoEditorText';
import {appText} from '../../../assets/text/appText';
import Button from '../../../uiToolkit/Buttons/Button';
import {BrowseMediaLibrary} from '../../../modules/BrowseMediaLibrary/';
import {ReactComponent as ImageTypeIcon} from '../../../assets/images/artifact-type-image.svg';
import {ReactComponent as VideoTypeIcon} from '../../../assets/images/artifact-type-video.svg';
import styles from './ReplaceAIMediaModal.module.css';
import {toast} from 'react-toastify';
import {toastConfig} from '../../../uiToolkit/Toast/toastConfig';

interface Props {
  shown: boolean;
  chunk: GeneratedVideoChunk | null;
  organizationId: string;
  onSave: (
    chunkId: string,
    replacements: {artifactUri: string; libraryMediaFileId: string}[],
  ) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const isImageUrl = (url: string) => {
  const lower = url.toLowerCase();
  return (
    lower.endsWith('.jpg') || lower.endsWith('.jpeg') || lower.endsWith('.png')
  );
};

const ReplaceAIMediaModal = React.memo(
  ({shown, chunk, organizationId, onSave, onCancel, isLoading}: Props) => {
    const [replacements, setReplacements] = useState<Map<string, MediaFile>>(
      new Map(),
    );
    const [pickingForArtifactUri, setPickingForArtifactUri] = useState<
      string | null
    >(null);

    useEffect(() => {
      if (shown) {
        setReplacements(new Map());
        setPickingForArtifactUri(null);
      }
    }, [shown, chunk?.id]);

    if (!shown || !chunk) return null;

    const artifacts = [...(chunk.artifacts || [])].sort(
      (a, b) => a.order - b.order,
    );

    const hasReplacements = replacements.size > 0;

    const handleSave = () => {
      if (!hasReplacements) return;
      const result = Array.from(replacements.entries()).map(
        ([artifactUri, file]) => ({
          artifactUri,
          libraryMediaFileId: file.id,
        }),
      );
      onSave(chunk.id, result);
    };

    const handleReplace = (artifact: ChunkArtifact) => {
      setPickingForArtifactUri(artifact.uri);
    };

    const handleRemove = (artifactUri: string) => {
      setReplacements(prev => {
        const next = new Map(prev);
        next.delete(artifactUri);
        return next;
      });
    };

    const isVideoFile = (file: MediaFile) =>
      (file.fileType || '').toLowerCase().includes('video');

    // Check if any OTHER artifact (excluding excludeUri) will be a video
    // in the final state: replacement type if replaced, original type otherwise
    const hasOtherVideo = (excludeUri: string) =>
      artifacts.some(a => {
        if (a.uri === excludeUri) return false;
        const replacement = replacements.get(a.uri);
        if (replacement) return isVideoFile(replacement);
        return !isImageUrl(a.uri);
      });

    const handleFileSelected = (file: MediaFile) => {
      if (!pickingForArtifactUri) return;

      if (isVideoFile(file) && hasOtherVideo(pickingForArtifactUri)) {
        toast.error(VideoEditorText.replaceAIMediaOnlyOneVideo, toastConfig);
        setPickingForArtifactUri(null);
        return;
      }

      setReplacements(prev => {
        const next = new Map(prev);
        next.set(pickingForArtifactUri, file);
        return next;
      });
      setPickingForArtifactUri(null);
    };

    return (
      <div className={styles.overlay}>
        <div className={styles.modal}>
          <h2 className={styles.title}>
            {VideoEditorText.replaceAIMediaModalTitle}
          </h2>

          <div className={styles.chunkPreview}>
            <video src={chunk.uri} controls />
          </div>

          {artifacts.length === 0 ? (
            <p className={styles.noArtifacts}>
              {VideoEditorText.replaceAIMediaNoArtifacts}
            </p>
          ) : (
            <div className={styles.artifactsList}>
              {artifacts.map((artifact, index) => {
                const replacement = replacements.get(artifact.uri);
                const artifactIsImage = isImageUrl(artifact.uri);

                return (
                  <div key={artifact.id} className={styles.artifactRow}>
                    <span className={styles.artifactOrder}>{index + 1}</span>
                    {artifactIsImage ? (
                      <ImageTypeIcon className={styles.typeIcon} />
                    ) : (
                      <VideoTypeIcon className={styles.typeIcon} />
                    )}

                    {artifactIsImage ? (
                      <img
                        src={artifact.publicUri}
                        className={styles.artifactThumbnail}
                        alt={`Artifact ${index + 1}`}
                      />
                    ) : (
                      <video
                        src={artifact.publicUri}
                        className={styles.artifactThumbnail}
                      />
                    )}

                    <span className={styles.arrow}>&rarr;</span>

                    <div className={styles.replacementArea}>
                      {replacement ? (
                        <>
                          {replacement.fileType?.includes('image') ? (
                            <img
                              src={replacement.fileUrl}
                              className={styles.replacementThumbnail}
                              alt="Replacement"
                            />
                          ) : (
                            <video
                              src={replacement.fileUrl}
                              className={styles.replacementThumbnail}
                            />
                          )}
                          <div className={styles.replacementActions}>
                            <button
                              className={styles.linkButton}
                              onClick={() => handleReplace(artifact)}
                              disabled={isLoading}>
                              {VideoEditorText.replaceAIMediaChange}
                            </button>
                            <button
                              className={`${styles.linkButton} ${styles.removeButton}`}
                              onClick={() => handleRemove(artifact.uri)}
                              disabled={isLoading}>
                              {VideoEditorText.replaceAIMediaRemove}
                            </button>
                          </div>
                        </>
                      ) : (
                        <div
                          className={styles.emptyReplacement}
                          onClick={() => !isLoading && handleReplace(artifact)}>
                          <span className={styles.emptyReplacementText}>
                            {VideoEditorText.replaceAIMediaReplace}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className={styles.buttons}>
            <Button
              title={appText.cancel}
              onClick={onCancel}
              uiType="text"
              disabled={isLoading}
            />
            <Button
              title={VideoEditorText.replaceAIMediaSave}
              onClick={handleSave}
              uiType="action"
              disabled={!hasReplacements || isLoading}
              isLoading={isLoading}
            />
          </div>
        </div>

        {pickingForArtifactUri && (
          <BrowseMediaLibrary
            organizationId={organizationId}
            onCancel={() => setPickingForArtifactUri(null)}
            onSelect={handleFileSelected}
            mode={hasOtherVideo(pickingForArtifactUri) ? 'images' : 'media'}
          />
        )}
      </div>
    );
  },
);

export default ReplaceAIMediaModal;
